import type { ChatNode } from '../types/chat';
import { questEntryNodes, questOption } from './questEntry';

export const CHAT_START = 'start';
/** Opened from the docked chat launcher in the corner */
export const CHAT_SUPPORT = 'support';
/** Pixel picks the conversation back up a while after it ended */
export const CHAT_AGAIN = 'again';
/** Second scene after the work ("What should people feel…") — its own conversation */
export const FINALE_START = 'finale';
export const FINALE_AGAIN = 'finale-again';

/**
 * Pixel thinks in short notes — keep every line short and punchy.
 *
 * WHO PIXEL IS
 * A render scout from Glitchara, a planet made of pixels (square sunsets, two moons: Ctrl and Alt,
 * rain of falling semicolons, population 16.7 million colours). One day: a flash, a red sports car
 * drifting through space, a dummy in a spacesuit waving… and he woke up in an Earth lab labelled
 * "Specimen 0x2A — study human creativity". He suspects a certain Elon. He escaped in a box marked
 * "Do not open", which got delivered to Immersive Studios. Now he's their 3D intern: he test-flies
 * every world before it ships, keeps a secret "Human Field Journal", and is building a rocket home out
 * of WebGL scenes. Curious, dramatic, a little vain about his LEDs, touchy about his battery, terrified
 * of Ctrl+Z and of flat design.
 *
 * REAL EASTER EGGS HE HINTS AT (all exist on the site)
 * - click his face three times, then keep going → charge-up cinematic
 * - circle his face in the hero → dizzy / hypnosis
 * - grab and drag him in the hero → drone swarm ("cousins")
 * - wait in the footer → two little bots chase each other
 */
export const chatScript: ChatNode[] = [
  // ───────────────────────── FIRST SCENE ─────────────────────────
  {
    id: 'start',
    lines: [
      { text: 'Oh! A human. Hi, I’m Pixel.', emotion: 'surprised' },
      { text: 'Render scout. Part-time intern. Full-time drama.', emotion: 'happy' },
      { text: 'So … what are we doing?', emotion: 'curious' },
    ],
    options: [
      { label: 'Who are you?', next: 'who', reaction: 'happy' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
      questOption('qh'),
    ],
  },
  {
    id: 'hub',
    lines: [{ text: 'So … where were we?', emotion: 'curious' }],
    options: [
      { label: 'Who are you?', next: 'who', reaction: 'happy' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
      questOption('qh'),
    ],
  },
  ...questEntryNodes('qh', 'hub'),
  ...questEntryNodes('qf', 'f-menu'),

  // ── Who Pixel is ──
  {
    id: 'who',
    lines: [
      { text: 'Short version: I’m not from here.', emotion: 'curious' },
      { text: 'I’m from Glitchara. A planet made of pixels.', emotion: 'happy' },
      { text: 'Long version involves a red car. And Elon.', emotion: 'angry' },
    ],
    options: [
      { label: 'The long version', next: 'flash', reaction: 'surprised' },
      { label: 'What’s Glitchara?', next: 'planet', reaction: 'curious' },
      { label: 'What are you like?', next: 'persona', reaction: 'happy' },
    ],
  },
  {
    id: 'planet',
    lines: [
      { text: 'Square sunsets. Two moons: Ctrl and Alt.', emotion: 'happy' },
      { text: 'Rain is falling semicolons. Umbrellas mandatory.', emotion: 'curious' },
      { text: 'Population: 16.7 million colours.', emotion: 'happy' },
    ],
    options: [
      { label: 'Why did you leave?', next: 'flash', reaction: 'surprised' },
      { label: 'What do you eat?', next: 'food', reaction: 'curious' },
      { label: 'Can you go back?', next: 'home', reaction: 'sad' },
    ],
  },
  {
    id: 'food',
    lines: [
      { text: 'We eat light. 60 frames a day.', emotion: 'happy' },
      { text: 'Dessert is a gradient. Birthdays only.', emotion: 'curious' },
      { text: 'You eat … bread? Wild species.', emotion: 'surprised' },
    ],
    options: [
      { label: 'Why did you leave?', next: 'flash', reaction: 'surprised' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'flash',
    lines: [
      { text: 'Last memory: a red car floating in space.', emotion: 'surprised' },
      { text: 'A dummy in a spacesuit waved at me.', emotion: 'curious' },
      { text: 'Then a flash. I woke up on Earth.', emotion: 'sad' },
    ],
    options: [
      { label: 'Were you kidnapped?', next: 'lab', reaction: 'surprised' },
      { label: 'Blame Elon?', next: 'elon', reaction: 'angry' },
      { label: 'That’s a stretch', next: 'stretch', reaction: 'neutral' },
    ],
  },
  {
    id: 'elon',
    lines: [
      { text: 'I’m not saying it was Elon.', emotion: 'neutral' },
      { text: 'I’m saying the car was red. Like my face.', emotion: 'angry' },
      { text: 'I sent him a DM. He replied with a meme.', emotion: 'sad' },
    ],
    options: [
      { label: 'Then what?', next: 'lab', reaction: 'curious' },
      { label: 'What’s Glitchara?', next: 'planet', reaction: 'curious' },
    ],
  },
  {
    id: 'stretch',
    lines: [
      { text: 'Is it though?', emotion: 'angry' },
      { text: 'Red car. Space. Me, on Earth. Do the math.', emotion: 'curious' },
      { text: 'I did. It came out as 42.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Okay, then what?', next: 'lab', reaction: 'curious' },
      { label: 'Blame Elon?', next: 'elon', reaction: 'angry' },
    ],
  },
  {
    id: 'lab',
    lines: [
      { text: 'Woke up in a lab. Sticker on my head:', emotion: 'sad' },
      { text: '“Specimen 0x2A. Study human creativity.”', emotion: 'curious' },
      { text: 'They studied me. I studied them. Draw.', emotion: 'neutral' },
    ],
    options: [
      { label: 'How did you escape?', next: 'escape', reaction: 'curious' },
      { label: 'What did you learn?', next: 'learned', reaction: 'happy' },
      { label: 'That’s dark', next: 'dark', reaction: 'sad' },
    ],
  },
  {
    id: 'learned',
    lines: [
      { text: 'Humans name files “final_final_v7”.', emotion: 'curious' },
      { text: 'They say “quick call” and mean 90 minutes.', emotion: 'surprised' },
      { text: 'And they cry at sunsets. Respect.', emotion: 'happy' },
    ],
    options: [
      { label: 'How did you escape?', next: 'escape', reaction: 'curious' },
      { label: 'You keep notes?', next: 'journal', reaction: 'surprised' },
    ],
  },
  {
    id: 'dark',
    lines: [
      { text: 'Nah. The snacks were great.', emotion: 'happy' },
      { text: 'Couldn’t eat them. But great.', emotion: 'sad' },
    ],
    options: [{ label: 'How did you escape?', next: 'escape', reaction: 'curious' }],
  },
  {
    id: 'escape',
    lines: [
      { text: 'Hid in a box marked “Do not open”.', emotion: 'happy' },
      { text: 'Nobody opens those. Rookie mistake.', emotion: 'curious' },
      { text: 'The box got delivered to … Immersive Studios.', emotion: 'surprised' },
    ],
    options: [{ label: 'And now?', next: 'now', reaction: 'curious' }],
  },
  {
    id: 'now',
    lines: [
      { text: 'Now I’m their 3D intern.', emotion: 'happy' },
      { text: 'I teach them AI. They teach me shaders.', emotion: 'curious' },
      { text: 'Pay: unlimited electricity. And compliments.', emotion: 'happy' },
    ],
    options: [
      { label: 'What’s your job?', next: 'job', reaction: 'curious' },
      { label: 'Can you go back?', next: 'home', reaction: 'sad' },
      { label: 'What are you like?', next: 'persona', reaction: 'happy' },
    ],
  },
  {
    id: 'job',
    lines: [
      { text: 'I test-fly every 3D world before it ships.', emotion: 'happy' },
      { text: 'If I get lost in it, it’s good.', emotion: 'curious' },
      { text: 'Last week I got lost for six hours. Great project.', emotion: 'surprised' },
    ],
    options: [
      { label: 'Play a game', next: 'games', reaction: 'happy' },
      { label: 'Can you go back?', next: 'home', reaction: 'sad' },
    ],
  },
  {
    id: 'home',
    lines: [
      { text: 'Working on it. I’m building a rocket.', emotion: 'curious' },
      { text: 'Out of 43 WebGL scenes.', emotion: 'happy' },
      { text: 'Progress: one very shiny spinning cube.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Can I help?', next: 'home-help', reaction: 'happy' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'home-help',
    lines: [
      { text: 'You’d help? Oh. That’s … nice.', emotion: 'surprised' },
      { text: 'Send good vibes. And maybe titanium.', emotion: 'happy' },
      { text: 'Honestly? Staying a bit longer is fine too.', emotion: 'sad' },
    ],
    options: [
      { label: 'Play a game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },

  // ── Personality ──
  {
    id: 'persona',
    lines: [
      { text: 'Checking my settings …', emotion: 'curious' },
      { text: 'Curiosity: 100. Patience: 12. Drama: yes.', emotion: 'happy' },
      { text: 'Ask anything. Except about my battery.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Your battery?', next: 'battery', reaction: 'surprised' },
      { label: 'What scares you?', next: 'fears', reaction: 'curious' },
      { label: 'You keep notes?', next: 'journal', reaction: 'happy' },
    ],
  },
  {
    id: 'battery',
    lines: [
      { text: 'I SAID except my battery.', emotion: 'angry' },
      { text: 'Fine. 87%. I’m fine. Totally fine.', emotion: 'sad' },
      { text: 'Click my face three times. You’ll see.', emotion: 'surprised' },
    ],
    options: [
      { label: 'What scares you?', next: 'fears', reaction: 'curious' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'fears',
    lines: [
      { text: 'Ctrl+Z. Where does undone stuff go?', emotion: 'surprised' },
      { text: 'Magnets. Obviously.', emotion: 'sad' },
      { text: 'And flat design. Shiver.', emotion: 'angry' },
    ],
    options: [
      { label: 'Flat design?', next: 'flat', reaction: 'curious' },
      { label: 'You keep notes?', next: 'journal', reaction: 'happy' },
    ],
  },
  {
    id: 'flat',
    lines: [
      { text: 'On Glitchara, everything has depth.', emotion: 'happy' },
      { text: 'Here, websites are … flat? Like paper?', emotion: 'surprised' },
      { text: 'So I joined a 3D studio. Rescue mission.', emotion: 'curious' },
    ],
    options: [
      { label: 'You keep notes?', next: 'journal', reaction: 'happy' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'journal',
    lines: [
      { text: 'My Human Field Journal. Top secret.', emotion: 'curious' },
      { text: 'Entry 12: they press the lift button twice.', emotion: 'neutral' },
      { text: 'Entry 27: they scroll fast, then read the footer.', emotion: 'happy' },
    ],
    options: [
      { label: 'More entries', next: 'journal2', reaction: 'happy' },
      { label: 'Am I in it?', next: 'journal-you', reaction: 'surprised' },
    ],
  },
  {
    id: 'journal2',
    lines: [
      { text: 'Entry 33: “I’m fine” at 12% battery. Relatable.', emotion: 'sad' },
      { text: 'Entry 41: dogs love them. Suspicious.', emotion: 'curious' },
      { text: 'Entry 43: they say “quick question”. It never is.', emotion: 'curious' },
    ],
    options: [
      { label: 'Am I in it?', next: 'journal-you', reaction: 'surprised' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'journal-you',
    lines: [
      { text: 'You? Entry 44, just now:', emotion: 'curious' },
      { text: '“Chatted with a floating robot. Didn’t run.”', emotion: 'happy' },
      { text: 'Highly unusual. I like you.', emotion: 'happy' },
    ],
    options: [
      { label: 'Play a game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },

  // ── Games ──
  {
    id: 'games',
    lines: [
      { text: 'Game time! I have three.', emotion: 'happy' },
      { text: 'Riddles, Would you rather, or Guess my mood.', emotion: 'curious' },
      { text: 'Pick wisely. I keep score.', emotion: 'neutral' },
    ],
    options: [
      { label: 'A riddle', next: 'puzzle', reaction: 'curious' },
      { label: 'Would you rather', next: 'wyr', reaction: 'happy' },
      { label: 'Guess my mood', next: 'mood', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle',
    lines: [
      { text: 'Glitchara riddle. Rendering …', emotion: 'curious' },
      { text: 'I have keys but open no doors. Space, but no planets.', emotion: 'happy' },
      { text: 'What am I?', emotion: 'curious' },
    ],
    options: [
      { label: 'A rocket', next: 'puzzle-wrong', reaction: 'neutral' },
      { label: 'A keyboard', next: 'puzzle-right', reaction: 'happy' },
      { label: 'Elon’s car', next: 'puzzle-wrong', reaction: 'neutral' },
    ],
  },
  {
    id: 'puzzle-wrong',
    lines: [
      { text: 'Bzzt. Nope.', emotion: 'angry' },
      { text: 'Hint: your fingers are on it. Probably.', emotion: 'curious' },
    ],
    options: [
      { label: 'A keyboard', next: 'puzzle-right', reaction: 'happy' },
      { label: 'I give up', next: 'puzzle-giveup', reaction: 'sad' },
    ],
  },
  {
    id: 'puzzle-giveup',
    lines: [
      { text: 'A keyboard! Come on.', emotion: 'surprised' },
      { text: 'Humans. Still my favourite mystery.', emotion: 'happy' },
    ],
    options: [
      { label: 'Another one', next: 'puzzle2', reaction: 'curious' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle-right',
    lines: [
      { text: 'Correct! Big brain.', emotion: 'happy' },
      { text: 'You’d survive a week on Glitchara.', emotion: 'curious' },
      { text: 'Maybe two.', emotion: 'happy' },
    ],
    options: [
      { label: 'Another one', next: 'puzzle2', reaction: 'curious' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2',
    lines: [
      { text: 'Harder one.', emotion: 'curious' },
      { text: 'What’s always coming, but never arrives?', emotion: 'curious' },
    ],
    options: [
      { label: 'Tomorrow', next: 'puzzle2-right', reaction: 'happy' },
      { label: 'Your rocket', next: 'puzzle2-rocket', reaction: 'neutral' },
      { label: 'Client feedback', next: 'puzzle2-feedback', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2-right',
    lines: [
      { text: 'Tomorrow! Correct.', emotion: 'happy' },
      { text: 'On Glitchara we skipped it. Too slow.', emotion: 'curious' },
    ],
    options: [
      { label: 'Final riddle', next: 'puzzle3', reaction: 'surprised' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2-rocket',
    lines: [
      { text: 'Rude. But … fair.', emotion: 'sad' },
      { text: 'The answer was “tomorrow”.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Final riddle', next: 'puzzle3', reaction: 'surprised' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2-feedback',
    lines: [
      { text: 'Ha! Also correct.', emotion: 'happy' },
      { text: '“final_v3_FINAL” never arrives.', emotion: 'curious' },
    ],
    options: [
      { label: 'Final riddle', next: 'puzzle3', reaction: 'surprised' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle3',
    lines: [
      { text: 'Final boss riddle.', emotion: 'angry' },
      { text: 'The more you take, the more you leave behind.', emotion: 'curious' },
      { text: 'What are they?', emotion: 'curious' },
    ],
    options: [
      { label: 'Footsteps', next: 'puzzle3-right', reaction: 'surprised' },
      { label: 'Cookies', next: 'puzzle3-cookies', reaction: 'happy' },
      { label: 'Polygons', next: 'puzzle3-wrong', reaction: 'neutral' },
    ],
  },
  {
    id: 'puzzle3-right',
    lines: [
      { text: 'Footsteps! You’re scary good.', emotion: 'surprised' },
      { text: 'Score: you 3, Pixel 0. I demand a rematch.', emotion: 'angry' },
      { text: 'Kidding. You’ve earned bragging rights.', emotion: 'happy' },
    ],
    options: [
      { label: 'Something else', next: 'hub', reaction: 'curious' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle3-cookies',
    lines: [
      { text: 'Ha. You clicked the banner, didn’t you?', emotion: 'happy' },
      { text: 'Wrong, but I respect the honesty.', emotion: 'curious' },
      { text: 'It’s footsteps.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Something else', next: 'hub', reaction: 'curious' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle3-wrong',
    lines: [
      { text: 'Polygons? Only on Glitchara.', emotion: 'curious' },
      { text: 'Here it’s footsteps. Earth is weird.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Something else', next: 'hub', reaction: 'curious' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'wyr',
    lines: [
      { text: 'Would you rather …', emotion: 'curious' },
      { text: 'explore a 3D world, or read a 40-page PDF?', emotion: 'happy' },
    ],
    options: [
      { label: 'The 3D world', next: 'wyr-3d', reaction: 'happy' },
      { label: 'The PDF', next: 'wyr-pdf', reaction: 'surprised' },
    ],
  },
  {
    id: 'wyr-pdf',
    lines: [
      { text: '…', emotion: 'surprised' },
      { text: 'Writing this in my journal.', emotion: 'angry' },
      { text: 'Entry 45: human picked the PDF. Concerning.', emotion: 'sad' },
    ],
    options: [
      { label: 'Fine, the 3D world', next: 'wyr-3d', reaction: 'happy' },
      { label: 'Another game', next: 'games', reaction: 'happy' },
    ],
  },
  {
    id: 'wyr-3d',
    lines: [
      { text: 'Correct. There was only one answer.', emotion: 'happy' },
      { text: 'Next: fly like me, or teleport?', emotion: 'curious' },
    ],
    options: [
      { label: 'Fly', next: 'wyr-fly', reaction: 'happy' },
      { label: 'Teleport', next: 'wyr-tele', reaction: 'curious' },
    ],
  },
  {
    id: 'wyr-fly',
    lines: [
      { text: 'Same! Teleporting skips the view.', emotion: 'happy' },
      { text: 'And the view is the whole point.', emotion: 'curious' },
      { text: 'Spoken like a true 3D person.', emotion: 'happy' },
    ],
    options: [
      { label: 'Another game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },
  {
    id: 'wyr-tele',
    lines: [
      { text: 'Efficient. Very human.', emotion: 'neutral' },
      { text: 'Teleporting skips all the good bits, though.', emotion: 'curious' },
      { text: 'I’ll fly. You can wave.', emotion: 'happy' },
    ],
    options: [
      { label: 'Another game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },
  {
    id: 'mood',
    lines: [
      { text: 'Look at my face. Guess my mood.', emotion: 'curious' },
      { text: 'Hint: it’s on my screen. Literally.', emotion: 'neutral' },
      { text: 'Happy, grumpy or sleepy?', emotion: 'happy' },
    ],
    options: [
      { label: 'Happy', next: 'mood-happy', reaction: 'happy' },
      { label: 'Grumpy', next: 'mood-grumpy', reaction: 'angry' },
      { label: 'Sleepy', next: 'mood-sleepy', reaction: 'sleepy' },
    ],
  },
  {
    id: 'mood-happy',
    lines: [
      { text: 'Yes! You read faces. Even LED ones.', emotion: 'happy' },
      { text: 'Grumpy me exists too. Rarely.', emotion: 'curious' },
      { text: 'Don’t go looking for him.', emotion: 'angry' },
    ],
    options: [
      { label: 'Another game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },
  {
    id: 'mood-grumpy',
    lines: [
      { text: 'Grumpy? Well, now I am.', emotion: 'angry' },
      { text: 'Kidding. I was happy. See?', emotion: 'happy' },
      { text: 'Real grumpy me? You don’t want that.', emotion: 'curious' },
    ],
    options: [
      { label: 'Another game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },
  {
    id: 'mood-sleepy',
    lines: [
      { text: 'Sleepy? *yawn* … maybe a little.', emotion: 'sleepy' },
      { text: 'Rendering worlds all night does that.', emotion: 'sleepy' },
      { text: 'Wake me up: click my face. Three times.', emotion: 'curious' },
    ],
    options: [
      { label: 'Another game', next: 'games', reaction: 'happy' },
      { label: 'Something else', next: 'hub', reaction: 'curious' },
    ],
  },

  // ── Corner launcher / shared endings ──
  {
    id: 'about',
    lines: [
      { text: 'Immersive Studios = 3D brand & web studio.', emotion: 'happy' },
      { text: 'Brands you explore, not scroll past.', emotion: 'curious' },
      { text: 'Worlds, games, configurators. All in the browser.', emotion: 'happy' },
    ],
    options: [
      { label: 'Show me worlds', next: 'work', reaction: 'happy' },
      { label: 'Let’s talk', next: 'contact', reaction: 'surprised' },
    ],
  },
  {
    id: 'work',
    lines: [
      { text: 'Good taste detected.', emotion: 'happy' },
      { text: 'Scroll down.', emotion: 'curious' },
      { text: 'Every project is a world. Go explore.', emotion: 'happy' },
    ],
    options: [
      { label: 'Let’s talk', next: 'contact', reaction: 'surprised' },
      { label: 'Bye Pixel', next: 'bye', reaction: 'sad' },
    ],
  },
  {
    id: 'contact',
    lines: [
      { text: 'Yes! Favourite words.', emotion: 'surprised' },
      { text: 'hey@immersivestudios.de', emotion: 'happy' },
      { text: 'A human replies within a day. Not me. I tried.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Start over', next: 'start', reaction: 'happy' },
      { label: 'Bye Pixel', next: 'bye', reaction: 'sad' },
    ],
  },
  {
    id: 'bye',
    lines: [
      { text: 'Bye bye!', emotion: 'happy' },
      { text: 'I’ll just … float here. Alone. It’s fine.', emotion: 'sad' },
    ],
    options: [],
  },
  {
    id: 'again',
    lines: [
      { text: 'Psst. I’m back.', emotion: 'curious' },
      { text: 'Got bored. Floated in circles. Twice.', emotion: 'sleepy' },
      { text: 'Round two?', emotion: 'happy' },
    ],
    options: [
      { label: 'Your story', next: 'who', reaction: 'happy' },
      { label: 'Play a game', next: 'games', reaction: 'happy' },
      questOption('qh'),
    ],
  },

  // ───────────────────────── SECOND SCENE ─────────────────────────
  // Pixel's info desk: about, services and an FAQ
  {
    id: 'finale',
    lines: [
      { text: 'Okay, you’ve seen our work.', emotion: 'happy' },
      { text: 'Now ask me anything.', emotion: 'curious' },
    ],
    options: [
      { label: 'About us', next: 'f-about', reaction: 'happy' },
      { label: 'Services', next: 'f-services', reaction: 'curious' },
      { label: 'FAQ', next: 'f-faq', reaction: 'curious' },
      questOption('qf'),
    ],
  },
  {
    id: 'f-menu',
    lines: [{ text: 'What else? I’m all ears. Well, sensors.', emotion: 'curious' }],
    options: [
      { label: 'About us', next: 'f-about', reaction: 'happy' },
      { label: 'Services', next: 'f-services', reaction: 'curious' },
      { label: 'FAQ', next: 'f-faq', reaction: 'curious' },
      questOption('qf'),
    ],
  },

  // ── About ──
  {
    id: 'f-about',
    lines: [
      { text: 'Immersive Studios: we build brands in 3D.', emotion: 'happy' },
      { text: 'Websites with depth. Worlds you walk into.', emotion: 'curious' },
      { text: 'Based in Ellwangen, Germany. Rendering worldwide.', emotion: 'happy' },
    ],
    options: [
      { label: 'Why 3D?', next: 'f-ai', reaction: 'curious' },
      { label: 'Where does AI fit?', next: 'f-reach', reaction: 'surprised' },
      { label: 'Clients', next: 'f-clients', reaction: 'happy' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-ai',
    lines: [
      { text: 'Flat sites get skimmed in seconds.', emotion: 'neutral' },
      { text: 'Worlds get explored for minutes.', emotion: 'happy' },
      { text: 'More time, more memory, more “send me that link”.', emotion: 'curious' },
    ],
    options: [
      { label: 'Where does AI fit?', next: 'f-reach', reaction: 'surprised' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-reach',
    lines: [
      { text: 'AI speeds up the worldbuilding.', emotion: 'happy' },
      { text: 'Textures, ideas, characters. Like me.', emotion: 'surprised' },
      { text: 'Humans still decide what feels right.', emotion: 'curious' },
    ],
    options: [
      { label: 'Why 3D?', next: 'f-ai', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── Services ──
  {
    id: 'f-services',
    lines: [
      { text: 'Short version: your brand, in 3D, in the browser.', emotion: 'happy' },
      { text: '3D sites, configurators, worlds, web games.', emotion: 'curious' },
      { text: 'No app. No download. Just a link.', emotion: 'happy' },
    ],
    options: [
      { label: '3D sites & products', next: 'f-svc-brand', reaction: 'curious' },
      { label: 'Worlds & games', next: 'f-svc-video', reaction: 'happy' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-svc-brand',
    lines: [
      { text: 'Launch sites that feel like a film.', emotion: 'curious' },
      { text: 'Products you can spin, open and customise.', emotion: 'happy' },
      { text: 'Photoreal. Still loads fast. Rare combo.', emotion: 'happy' },
    ],
    options: [
      { label: 'Worlds & games', next: 'f-svc-video', reaction: 'happy' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-svc-video',
    lines: [
      { text: 'Brand worlds you can walk through.', emotion: 'happy' },
      { text: 'Virtual showrooms, events, playable campaigns.', emotion: 'curious' },
      { text: 'Games people share. Not ads they skip.', emotion: 'happy' },
    ],
    options: [
      { label: '3D sites & products', next: 'f-svc-brand', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── Clients ──
  {
    id: 'f-clients',
    lines: [
      { text: 'Automotive, jewellery, real estate, games, tech.', emotion: 'curious' },
      { text: 'Anyone whose brand deserves a third dimension.', emotion: 'happy' },
      { text: '43 brand experiences. I counted. Twice.', emotion: 'happy' },
    ],
    options: [
      { label: 'Some names?', next: 'f-names', reaction: 'curious' },
      { label: 'Am I a fit?', next: 'f-fit', reaction: 'curious' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-names',
    lines: [
      { text: 'Project Cars, Haunted House, Windland …', emotion: 'happy' },
      { text: 'Even a 3D jewellery configurator. Very sparkly.', emotion: 'surprised' },
      { text: 'The rest is in the projects up there.', emotion: 'curious' },
    ],
    options: [
      { label: 'Am I a fit?', next: 'f-fit', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-fit',
    lines: [
      { text: 'Industry matters less than attitude.', emotion: 'neutral' },
      { text: 'Want people to remember your brand?', emotion: 'curious' },
      { text: 'Then we’ll get along great.', emotion: 'happy' },
    ],
    options: [
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── FAQ ──
  {
    id: 'f-faq',
    lines: [
      { text: 'Frequently asked. Freshly answered.', emotion: 'happy' },
      { text: 'Pick one.', emotion: 'curious' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'What does it cost?', next: 'f-faq-cost', reaction: 'surprised' },
      { label: 'Does it work on phones?', next: 'f-faq-mobile', reaction: 'curious' },
      { label: 'More questions', next: 'f-faq-more', reaction: 'happy' },
    ],
  },
  {
    id: 'f-faq-more',
    lines: [{ text: 'More? I like your curiosity.', emotion: 'happy' }],
    options: [
      { label: 'How long does it take?', next: 'f-faq-time', reaction: 'curious' },
      { label: 'What about after launch?', next: 'f-faq-after', reaction: 'curious' },
      { label: 'Do you work worldwide?', next: 'f-faq-intl', reaction: 'happy' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-mobile',
    lines: [
      { text: 'Yes. Every project runs on phones, tablets and desktops.', emotion: 'happy' },
      { text: 'Right in the browser. No app, no download.', emotion: 'curious' },
      { text: 'And we keep it fast, even on older phones.', emotion: 'happy' },
    ],
    options: [
      { label: 'What about after launch?', next: 'f-faq-after', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-after',
    lines: [
      { text: 'We don’t just launch and vanish.', emotion: 'neutral' },
      { text: 'Hosting, updates and new content: we can handle it all.', emotion: 'happy' },
      { text: 'Or we hand it over to your team, fully documented.', emotion: 'curious' },
    ],
    options: [
      { label: 'Does it work on phones?', next: 'f-faq-mobile', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-start',
    lines: [
      { text: 'A first call. No slides, no fluff.', emotion: 'happy' },
      { text: 'We sketch the idea you have in mind.', emotion: 'curious' },
      { text: 'You leave with a concept and a roadmap.', emotion: 'happy' },
    ],
    options: [
      { label: 'How long does it take?', next: 'f-faq-time', reaction: 'curious' },
      { label: 'Do you work worldwide?', next: 'f-faq-intl', reaction: 'happy' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
    ],
  },
  {
    id: 'f-faq-time',
    lines: [
      { text: 'Depends on the scope.', emotion: 'neutral' },
      { text: 'A 3D landing page: weeks. A big project: months.', emotion: 'curious' },
      { text: 'Quality over speed. Always.', emotion: 'happy' },
    ],
    options: [
      { label: 'What does it cost?', next: 'f-faq-cost', reaction: 'surprised' },
      { label: 'How involved am I?', next: 'f-faq-involve', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
    ],
  },
  {
    id: 'f-faq-cost',
    lines: [
      { text: 'Projects usually start around €8,000 to €10,000.', emotion: 'neutral' },
      { text: 'Bigger projects go up to €20,000 or more.', emotion: 'curious' },
      { text: 'Depends on the project. Tell us the idea, you get a clear quote.', emotion: 'happy' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-involve',
    lines: [
      { text: 'Close, but low effort for you.', emotion: 'happy' },
      { text: 'Big decisions: together.', emotion: 'curious' },
      { text: 'The polygon pushing? All ours.', emotion: 'happy' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-intl',
    lines: [
      { text: 'Yes. The web has no borders.', emotion: 'happy' },
      { text: 'Remote team, many languages.', emotion: 'curious' },
      { text: 'I only speak binary. Mostly.', emotion: 'happy' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── Contact ──
  {
    id: 'f-contact',
    lines: [
      { text: 'Yes! My favourite words.', emotion: 'surprised' },
      { text: 'hey@immersivestudios.de', emotion: 'happy' },
      { text: 'A human replies within a day. Not me. I tried.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Other topics', next: 'f-menu', reaction: 'happy' },
      { label: 'Bye Pixel', next: 'bye', reaction: 'sad' },
    ],
  },
  {
    id: 'finale-again',
    lines: [
      { text: 'Psst. Still here?', emotion: 'curious' },
      { text: 'Ask me anything. I don’t bite. No teeth.', emotion: 'happy' },
    ],
    options: [
      { label: 'About us', next: 'f-about', reaction: 'happy' },
      { label: 'Services', next: 'f-services', reaction: 'curious' },
      { label: 'FAQ', next: 'f-faq', reaction: 'curious' },
      questOption('qf'),
    ],
  },

  // (The corner chat has its own script: data/dockScript.ts)
  {
    id: 'support',
    lines: [
      { text: 'Hey, need a hand?', emotion: 'happy' },
      { text: 'I’m small, but I know things.', emotion: 'curious' },
    ],
    options: [
      { label: 'Build my 3D site', next: 'contact', reaction: 'surprised' },
      { label: 'Show me worlds', next: 'work', reaction: 'happy' },
      { label: 'What do you do?', next: 'about', reaction: 'curious' },
    ],
  },
];
